/**
 * Normalization wizard (spec §2 item 1): per-storey 2D plan of space
 * footprints, color-coded by occupancy; click a space to edit occupancy
 * override, exit flag, exclusion tier, and ambient dBA. Edits PATCH
 * immediately (optimistic, reverted on failure).
 */
import { useEffect, useMemo, useState } from "react";
import { useParams } from "@tanstack/react-router";
import { PlanView } from "../components/PlanView";
import { SpacePanel } from "../components/SpacePanel";
import { useIstariStore } from "../state/store";
import { effectiveOccupancy, joinFootprints, occupancyColor, storeyTabs } from "../wizard/plan";

/**
 * Active ruleset for the secondary subtype picker (ENG-14678 / ADR 0011).
 * Hard-coded for v1 — ASHRAE 170 is the only ruleset that declares subtypes
 * today. A future ticket will introduce a project-level ruleset selector
 * (paired with `/api/capabilities`) and source this from the project state.
 */
const ACTIVE_RULESET_ID = "ashrae-170-2021";

export function NormalizePage() {
  const params = useParams({ strict: false }) as { modelId?: string };
  const modelId = params.modelId ?? "";

  const spaces = useIstariStore((s) => s.spaces[modelId]);
  const status = useIstariStore((s) => s.spacesStatus[modelId] ?? "idle");
  const loadSpaces = useIstariStore((s) => s.loadSpaces);
  const patchSpace = useIstariStore((s) => s.patchSpace);
  const patchError = useIstariStore((s) => s.spacePatchError);
  const autoAssign = useIstariStore((s) => s.autoAssign);
  const occupancyVocab = useIstariStore((s) => s.occupancyVocab);
  const loadOccupancyVocab = useIstariStore((s) => s.loadOccupancyVocab);
  const loadOccupancyVocabForRuleset = useIstariStore(
    (s) => s.loadOccupancyVocabForRuleset,
  );
  const summary = useIstariStore(
    (s) => s.models.find((m) => m.modelId === modelId)?.summary ?? null,
  );

  const [activeStorey, setActiveStorey] = useState<number | null | undefined>(undefined);
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null);
  const [autoBusy, setAutoBusy] = useState(false);
  const [autoSummary, setAutoSummary] = useState<string | null>(null);

  const runAutoAssign = async (options: { occupancy: boolean; exits: boolean }) => {
    setAutoBusy(true);
    setAutoSummary(null);
    const result = await autoAssign(modelId, options);
    setAutoBusy(false);
    if (result) {
      setAutoSummary(
        `Auto-assigned ${result.occupancy_assigned} occupancies · marked ` +
          `${result.exits_marked} exits (seed ${result.seed})`,
      );
    }
  };

  useEffect(() => {
    if (modelId && status === "idle") void loadSpaces(modelId);
  }, [modelId, status, loadSpaces]);

  useEffect(() => {
    void loadOccupancyVocab();
    void loadOccupancyVocabForRuleset(ACTIVE_RULESET_ID);
  }, [loadOccupancyVocab, loadOccupancyVocabForRuleset]);

  const rows = useMemo(() => spaces ?? [], [spaces]);
  const tabs = useMemo(() => storeyTabs(rows), [rows]);
  const storey = activeStorey !== undefined ? activeStorey : tabs[0]?.storeyId;
  const planSpaces = useMemo(
    () =>
      joinFootprints(
        rows.filter((s) => s.storey_ifc_id === (storey ?? null)),
        summary,
      ),
    [rows, storey, summary],
  );
  const selected = rows.find((s) => s.id === selectedSpaceId) ?? null;

  const usedOccupancies = useMemo(() => {
    const used = new Set(rows.map(effectiveOccupancy).filter((o): o is string => o !== null));
    if (occupancyVocab === null) return [];
    return occupancyVocab.map((v) => v.key).filter((k) => used.has(k));
  }, [rows, occupancyVocab]);

  return (
    <main
      style={{
        fontFamily: "sans-serif",
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        boxSizing: "border-box",
        padding: "1rem 1.5rem",
        background: "#1a1a2e",
        color: "#d8d8e8",
      }}
    >
      <nav style={{ marginBottom: "0.6rem", display: "flex", gap: "1rem" }}>
        <a href={`/models/${modelId}/readiness`} style={{ color: "#8e8eb8" }}>
          ← Readiness report
        </a>
        <a
          data-testid="normalize-mep-link"
          href={`/models/${modelId}/mep`}
          style={{ color: "#4a90d9" }}
        >
          MEP generation →
        </a>
      </nav>
      <h1 style={{ margin: "0 0 0.6rem", fontSize: "1.2rem" }}>Normalize spaces</h1>

      {status === "loading" && <p>Loading spaces…</p>}
      {status === "error" && (
        <div data-testid="spaces-error" style={{ color: "#e06c75" }}>
          <p>Could not load spaces for this model.</p>
          <button
            type="button"
            onClick={() => void loadSpaces(modelId)}
            style={{
              padding: "0.4rem 0.8rem",
              borderRadius: 6,
              border: "1px solid #4a90d9",
              background: "transparent",
              color: "#4a90d9",
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      )}
      {status === "ready" && rows.length === 0 && (
        <p data-testid="spaces-empty" style={{ color: "#8e8eb8" }}>
          This model has no spaces — draw them in a future wave (TODO-Wave3).
        </p>
      )}

      {rows.length > 0 && (
        <>
          <div
            style={{
              display: "flex",
              gap: "0.6rem",
              alignItems: "center",
              marginBottom: "0.6rem",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              data-testid="auto-occupancy-button"
              onClick={() => void runAutoAssign({ occupancy: true, exits: false })}
              disabled={autoBusy}
              style={{
                padding: "0.35rem 0.8rem",
                borderRadius: 6,
                border: "1px solid #4a90d9",
                background: "transparent",
                color: "#4a90d9",
                cursor: autoBusy ? "wait" : "pointer",
              }}
            >
              Auto-assign occupancy
            </button>
            <button
              type="button"
              data-testid="auto-exits-button"
              onClick={() => void runAutoAssign({ occupancy: false, exits: true })}
              disabled={autoBusy}
              style={{
                padding: "0.35rem 0.8rem",
                borderRadius: 6,
                border: "1px solid #4a90d9",
                background: "transparent",
                color: "#4a90d9",
                cursor: autoBusy ? "wait" : "pointer",
              }}
            >
              Auto-mark exits
            </button>
            {autoSummary && (
              <span data-testid="auto-assign-result" style={{ color: "#8e8eb8", fontSize: "0.82rem" }}>
                {autoSummary}
              </span>
            )}
          </div>

          <div
            data-testid="storey-tabs"
            style={{
              display: "flex",
              gap: "0.4rem",
              marginBottom: "0.6rem",
              flexWrap: "wrap",
            }}
          >
            {tabs.map((tab) => {
              const active = (storey ?? null) === tab.storeyId;
              return (
                <button
                  key={tab.storeyId ?? "none"}
                  type="button"
                  data-testid="storey-tab"
                  onClick={() => {
                    setActiveStorey(tab.storeyId);
                    setSelectedSpaceId(null);
                  }}
                  style={{
                    padding: "0.35rem 0.8rem",
                    borderRadius: 6,
                    border: "none",
                    cursor: "pointer",
                    background: active ? "#4a90d9" : "#2a2a44",
                    color: active ? "#fff" : "#d8d8e8",
                  }}
                >
                  {tab.name} ({tab.spaceCount})
                </button>
              );
            })}
          </div>

          {patchError && (
            <p data-testid="patch-error" style={{ color: "#e06c75" }}>
              Change not saved (reverted): {patchError}
            </p>
          )}

          <div style={{ display: "flex", gap: "1rem", flex: 1, minHeight: 0 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <PlanView
                spaces={planSpaces}
                selectedSpaceId={selectedSpaceId}
                onSelect={setSelectedSpaceId}
              />
              {usedOccupancies.length > 0 && (
                <div
                  data-testid="occupancy-legend"
                  style={{
                    display: "flex",
                    gap: "0.8rem",
                    flexWrap: "wrap",
                    marginTop: "0.5rem",
                    fontSize: "0.75rem",
                    color: "#8e8eb8",
                  }}
                >
                  {usedOccupancies.map((kind) => (
                    <span
                      key={kind}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.3rem",
                      }}
                    >
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 2,
                          background: occupancyColor(kind),
                          display: "inline-block",
                        }}
                      />
                      {kind.replace(/_/g, " ")}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <aside
              style={{
                width: 300,
                flexShrink: 0,
                background: "#21213a",
                borderRadius: 8,
                padding: "0.9rem",
                overflowY: "auto",
              }}
            >
              {selected ? (
                <SpacePanel
                  key={selected.id}
                  space={selected}
                  onPatch={(patch) => void patchSpace(modelId, selected.id, patch)}
                  rulesetId={ACTIVE_RULESET_ID}
                />
              ) : (
                <p data-testid="no-space-selected" style={{ color: "#8e8eb8" }}>
                  Click a space on the plan to edit its occupancy, exit flag, exclusion tier, or
                  ambient dBA.
                </p>
              )}
            </aside>
          </div>
        </>
      )}
    </main>
  );
}
